// src/views/ProductsView.jsx
import { Suspense, lazy, useState, useEffect, useRef } from '@wordpress/element';
import { shortcodeAttsHelper, consoleDebug } from '../util';
import useFetchRest from '../hooks/useFetchRest';
import LoadingSpinner from '../components/LoadingSpinner';
import { ErrorBoundary } from '@sentry/react';
import ErrorComponent from '../ErrorComponent';
import { useConfig } from '../contexts/ConfigContext';
import { useAppState } from '../contexts/AppStateContext';
import { useSpecialsList } from '../hooks/dutchie-queries.js';
import SingleProduct from './SingleProduct.jsx';

const ProductsGrid = lazy(() =>
    import('../components/ProductsView/ProductsGrid')
);
const CategoriesGrid = lazy(() =>
    import('../components/ProductsView/CategoriesGrid')
);
const SpecialsGrid = lazy(() =>
    import('../components/ProductsView/SpecialsGrid')
);
const BrandsGrid = lazy(() =>
    import('../components/ProductsView/BrandsGrid.jsx')
);

const ProductsView = ({
    shortcodeData,
    rootId,
}) => {
    // Track the current retailer ID to detect changes
    const previousRetailerId = useRef(null);
    
    // Get app state values
    const { retailerId, productPopupData, setProductPopupData } = useAppState();
    
    // Get all config values from useConfig
    const {
        retailers,
        styleOptions,
        surfside,
        menuConfig,
        menuFilter,
        analytics,
        nonHeadlessRetailer
    } = useConfig();
    
    // Compute derived values
    const currentRetailer = retailers[retailerId];
    
    // Create state variables with default values
    const [isRetailerLoading, setIsRetailerLoading] = useState(false);
    const [loadingState, setLoadingState] = useState(null);
    const [specialProductsIds, setSpecialProductsIds] = useState({});
    const [filteredSpecialProductsIds, setFilteredSpecialProductsIds] = useState({});
    const [filteredSpecialsList, setFilteredSpecialsList] = useState([]);
    const [safeShortcodeData, setSafeShortcodeData] = useState({ shortcodeAtts: {}, shortcode: '' });
    const [safeAtts, setSafeAtts] = useState({
        menuFilter: {},
        priceMin: null,
        priceMax: null,
    });

    // Extract safe retailer values from currentRetailer
    const safeRetailerValues = {
        menuSlug: currentRetailer?.menuSlug || '',
        brandTerms: currentRetailer?.brandTerms || []
    };

    // Detect retailer changes and reset state
    useEffect(() => {
        if (previousRetailerId.current !== null && previousRetailerId.current !== retailerId) {
            consoleDebug('🔄 Retailer changed, resetting state', {
                from: previousRetailerId.current,
                to: retailerId
            });
            
            // Set loading state while new data is fetched
            setIsRetailerLoading(true);
            
            // Reset all data that depends on retailer
            setSpecialProductsIds({});
            setFilteredSpecialProductsIds({});
            setFilteredSpecialsList([]);
            
            // Force re-fetch by clearing previous data
            setTimeout(() => {
                setIsRetailerLoading(false);
            }, 100);
        }
        
        previousRetailerId.current = retailerId;
    }, [retailerId]);

    // Initialize with safe defaults and check for non-headless retailer
    useEffect(() => {
        if (nonHeadlessRetailer) {
            setLoadingState(true);
            return;
        }
        
        // Process shortcode data safely
        if (shortcodeData && typeof shortcodeData === 'object') {
            setSafeShortcodeData({
                shortcodeAtts: shortcodeData.shortcodeAtts || {},
                shortcode: shortcodeData.shortcode || ''
            });
            
            // Process shortcode attributes
            try {
                const processedAtts = shortcodeAttsHelper(shortcodeData.shortcodeAtts || {});
                setSafeAtts({
                    ...processedAtts,
                    menuFilter: processedAtts.menuFilter || {},
                    priceMin: processedAtts.priceMin || null,
                    priceMax: processedAtts.priceMax || null,
                });
            } catch (error) {
                console.error('Error processing shortcode attributes:', error);
                setSafeAtts({
                    menuFilter: {},
                    priceMin: null,
                    priceMax: null,
                });
            }
        }
    }, [nonHeadlessRetailer, shortcodeData]);

    // Early return for non-headless retailer or loading state
    if (nonHeadlessRetailer || loadingState || isRetailerLoading) {
        return <LoadingSpinner />;
    }

    // Fetch specials list - skip if no retailerId
    const { data: specialsListData, isFetched: isFetchedSpecialsList, isLoading: isLoadingSpecials } =
        useSpecialsList(retailerId);

    // Fetch menu data - skip if no retailerId
    const { data: menuData, isFetched: isFetchedMenuData, isLoading: isLoadingMenu } = useFetchRest(
        retailerId ? `data/menu?retailerId=${retailerId}` : null,
        { enabled: !!retailerId }
    );

    // Show loading state while critical data is being fetched
    const isDataLoading = isLoadingSpecials || isLoadingMenu;

    /**
     * Filter valid special products based on the specials list
     */
    const filterValidSpecialProducts = (specialProductsIds, specialsList) => {
        consoleDebug('💈 filterValidSpecialProducts Debug - Start');

        // Check if inputs are valid
        if (!specialProductsIds || typeof specialProductsIds !== 'object' || !specialsList || !Array.isArray(specialsList)) {
            consoleDebug('Invalid inputs provided');
            return {};
        }

        // Get counts using Object.keys()
        const unfilteredCount = Object.keys(specialProductsIds).length;
        consoleDebug('Number of unfiltered special products:', unfilteredCount);

        if (menuConfig?.showEmptySpecials) {
            consoleDebug('Not applying filter, returning:', specialProductsIds);
            return specialProductsIds;
        }

        // Otherwise, apply the original filtering logic with additional safety checks
        const validSpecialIds = new Set(
            specialsList
                .filter(special => special && special.id)
                .map(special => special.id)
        );

        const filteredSpecialsObject = Object.fromEntries(
            Object.entries(specialProductsIds)
                .filter(([specialId]) => specialId && validSpecialIds.has(specialId))
        );

        // Get count of filtered items
        const filteredCount = Object.keys(filteredSpecialsObject).length;

        consoleDebug('Unfiltered valid products list:', specialProductsIds, `(${unfilteredCount} items)`);
        consoleDebug('Applying filter to specials:', filteredSpecialsObject, `(${filteredCount} items)`);
        consoleDebug('Items removed by filter:', unfilteredCount - filteredCount);
        consoleDebug('💈 filterValidSpecialProducts Debug - End');

        return filteredSpecialsObject;
    };

    // Update special products from menu data
    useEffect(() => {
        if (menuData && isFetchedMenuData && retailerId) {
            consoleDebug('📦 Setting special products IDs for retailer:', retailerId);
            setSpecialProductsIds(menuData.specialProductsIds || {});
        }
    }, [menuData, isFetchedMenuData, retailerId]);

    // Filter special products when specials list is available
    useEffect(() => {
        if (specialsListData && specialProductsIds && retailerId) {
            const filtered = filterValidSpecialProducts(specialProductsIds, specialsListData);
            setFilteredSpecialProductsIds(filtered);
        }
    }, [specialsListData, specialProductsIds, retailerId, menuConfig?.showEmptySpecials]);

    // Filter out specials that have no products
    useEffect(() => {
        consoleDebug('💈 Empty Special Products Filter Debug - Start');
        if (specialsListData && filteredSpecialProductsIds && retailerId) {
            if (menuConfig?.showEmptySpecials) {
                // Only show specials that exist in filteredSpecialProductsIds
                const availableSpecials = specialsListData.filter(special =>
                    special && special.id &&
                    Object.keys(filteredSpecialProductsIds).includes(special.id)
                );
                consoleDebug('Showing available specials:', availableSpecials.length);
                setFilteredSpecialsList(availableSpecials);
                return;
            }

            // Original filtering logic for when showEmptySpecials is false
            const filteredSpecials = specialsListData.filter(special => {
                if (!special || !special.id) return false;
                const specialProductIds = filteredSpecialProductsIds[special.id] || [];
                const hasProducts = specialProductIds.length > 0;
                return hasProducts;
            });

            if (filteredSpecials) {
                consoleDebug('Unfiltered list: ', specialsListData.length);
                consoleDebug('Returning filtered specials list: ', filteredSpecials.length);
                setFilteredSpecialsList(filteredSpecials);
            } else {
                setFilteredSpecialsList(specialsListData || []);
            }
        }
        consoleDebug('💈 Empty Special Products Filter Debug - End');
    }, [specialsListData, filteredSpecialProductsIds, menuConfig?.showEmptySpecials, retailerId]);

    /**
     * Build the slider's menu filter based on shortcode atts.
     */
    const productsGridFilter = {
        ...menuFilter,
        brandIds: safeAtts.menuFilter?.brandIds ?? [],
        category: safeAtts.menuFilter?.category ?? [],
        subcategory: safeAtts.menuFilter?.subcategory ?? '',
        effects: safeAtts.menuFilter?.effects ?? [],
        strainType: safeAtts.menuFilter?.strainType ?? '',
        potencyCbd: safeAtts.menuFilter?.potencyCbd ?? menuFilter?.potencyCbd ?? {},
        potencyThc: safeAtts.menuFilter?.potencyThc ?? menuFilter?.potencyThc ?? {},
        weights: safeAtts.menuFilter?.weights ?? [],
        tags: safeAtts.menuFilter?.tags ?? [],
    };

    /**
     * If a brand is included in the shortcode atts, lookup its terms using the provided ID.
     */
    const productsGridBrand =
        safeAtts.menuFilter?.brandIds !== null &&
            Array.isArray(safeAtts.menuFilter?.brandIds) &&
            safeAtts.menuFilter?.brandIds.length > 0 &&
            Array.isArray(safeRetailerValues.brandTerms)
            ? safeRetailerValues.brandTerms.find(
                (brand) => brand && brand.id === safeAtts.menuFilter?.brandIds[0]
            )
            : null;

    /**
     * Whether or not a slider will have price filters.
     */
    const { priceMin, priceMax } = safeAtts;
    const priceFilters =
        priceMin || priceMax
            ? {
                priceMin: priceMin ? Number(priceMin) : null,
                priceMax: priceMax ? Number(priceMax) : null,
            }
            : null;

    // Don't render grids until we have the necessary data
    const isReadyToRender = retailerId && !isDataLoading && (
        !safeAtts?.specials || isFetchedSpecialsList
    );

    if (!isReadyToRender) {
        return <LoadingSpinner />;
    }

    return (
        <>
            {safeAtts?.menuFilter?.brandSlider && (
                <ErrorBoundary
                    showDialog
                    fallback={(props) => <ErrorComponent {...props} />}
                    beforeCapture={(scope) => {
                        scope.setTag('shortcode', safeShortcodeData.shortcode || '');
                        scope.setTag('component', 'BrandsGrid');
                    }}
                >
                    <Suspense fallback={<LoadingSpinner />}>
                        <BrandsGrid
                            atts={safeAtts || {}}
                            brandImages={menuConfig?.brandImages || {}}
                            menuSlug={safeRetailerValues.menuSlug || ''}
                            rootId={rootId || ''}
                        />
                    </Suspense>
                </ErrorBoundary>
            )}

            {safeAtts?.menuFilter?.category && Array.isArray(safeAtts.menuFilter.category) &&
                safeAtts.menuFilter.category.includes('ALL') && (
                    <ErrorBoundary
                        showDialog
                        fallback={(props) => <ErrorComponent {...props} />}
                        beforeCapture={(scope) => {
                            scope.setTag('shortcode', safeShortcodeData.shortcode || '');
                            scope.setTag('component', 'CategoriesGrid');
                        }}
                    >
                        <Suspense fallback={<LoadingSpinner />}>
                            <CategoriesGrid
                                atts={safeAtts || {}}
                                images={menuConfig?.categoryImages || {}}
                                categories={Array.isArray(menuConfig?.categoriesToShow) 
                                    ? menuConfig.categoriesToShow 
                                    : []}
                                menuSlug={safeRetailerValues.menuSlug || ''}
                                rootId={rootId || ''}
                            />
                        </Suspense>
                    </ErrorBoundary>
                )}

            {safeAtts?.specials && (
                isFetchedSpecialsList ? (
                    <ErrorBoundary
                        showDialog
                        fallback={(props) => <ErrorComponent {...props} />}
                        beforeCapture={(scope) => {
                            scope.setTag('shortcode', safeShortcodeData.shortcode || '');
                            scope.setTag('component', 'SpecialsGrid');
                        }}
                    >
                        <Suspense fallback={<LoadingSpinner />}>
                            {safeAtts.specialsType === 'CARDS' ? (
                                <SpecialsGrid
                                    specials={Array.isArray(filteredSpecialsList) ? filteredSpecialsList : []}
                                    atts={safeAtts || {}}
                                    menuSlug={safeRetailerValues.menuSlug || ''}
                                    styleOptions={styleOptions || {}}
                                    rootId={rootId || ''}
                                />
                            ) : (
                                <ProductsGrid
                                    key={`products-grid-specials-${retailerId}`}
                                    atts={safeAtts || {}}
                                    filter={productsGridFilter || menuFilter || {}}
                                    menuSlug={safeRetailerValues.menuSlug || ''}
                                    brand={productsGridBrand || null}
                                    rootId={rootId || ''}
                                    styleOptions={styleOptions || {}}
                                    surfside={surfside || null}
                                    specialProductsIds={filteredSpecialProductsIds || {}}
                                    priceFilters={priceFilters || null}
                                    gtmId={analytics?.gtm_id}
                                />
                            )}
                        </Suspense>
                    </ErrorBoundary>
                ) : (
                    <LoadingSpinner loadingType='slider-smaller' />
                )
            )}

            {/* Continue with other grid renders, updating references similarly */}

            {(!safeAtts?.menuFilter?.category || !Array.isArray(safeAtts?.menuFilter?.category) ||
                !safeAtts?.menuFilter?.category.includes('ALL')) &&
                !safeAtts?.menuFilter?.brandSlider &&
                !safeAtts?.specials &&
                !safeAtts?.special && (
                    <ErrorBoundary
                        showDialog
                        fallback={(props) => <ErrorComponent {...props} />}
                        beforeCapture={(scope) => {
                            scope.setTag('shortcode', safeShortcodeData.shortcode || '');
                            scope.setTag('component', 'ProductsGrid');
                            scope.setTag(
                                'menuFilter',
                                JSON.stringify(productsGridFilter || {})
                            );
                        }}
                    >
                        <Suspense fallback={<LoadingSpinner />}>
                            <ProductsGrid
                                key={`products-grid-default-${retailerId}`}
                                atts={safeAtts || {}}
                                filter={productsGridFilter || menuFilter || {}}
                                menuSlug={safeRetailerValues.menuSlug || ''}
                                brand={productsGridBrand || null}
                                rootId={rootId || ''}
                                styleOptions={styleOptions || {}}
                                surfside={surfside || null}
                                specialProductsIds={filteredSpecialProductsIds || {}}
                                priceFilters={priceFilters || null}
                                gtmId={analytics?.gtm_id}
                            />
                        </Suspense>
                    </ErrorBoundary>
                )}

            {/* Rest of the grid renders follow the same pattern */}

            {productPopupData && (
                <SingleProduct
                    productPopupData={productPopupData}
                    setProductPopupData={setProductPopupData || (() => { })}
                    specialProductsIds={filteredSpecialProductsIds || {}}
                />
            )}
        </>
    );
};

export default ProductsView;
